// src/security/permissions.ts
// Central, testable permission model for:
// - Navigation rendering
// - Route guards
// - Button/action gating
//
// Assumptions:
// - Roles are additive: a user can have multiple roles.
// - Captains are team-scoped (they can captain one or more teams).
// - Everything is club-scoped (you’ve already moved to Clubs/{clubId}/...).
// - Module toggles live in club settings.
//
// Keep this file pure (no Firebase imports). Makes it easy to unit test.

export type Role = "Player" | "Captain" | "Treasurer" | "Admin";

export type ClubModules = {
  financeEnabled: boolean;
  finesEnabled: boolean;
  finesCreateTransactions: boolean; // only meaningful if financeEnabled === true
};

export type MemberContext = {
  uid: string;
  clubId: string;

  roles: Role[];

  /** Teams this member belongs to (for default filtering) */
  teamIds: string[];

  /** Teams this member is captain of (subset of teamIds, but not necessarily) */
  captainTeamIds: string[];

  /** Convenience flags if you have them */
  isActiveMember?: boolean;

  /** Club module toggles */
  modules: ClubModules;
};

export type FixtureContext = {
  fixtureId: string;
  seasonId: string;
  teamId: string;
};

export type PlayerContext = {
  playerId: string;
  /** If this is the player record linked to the current user */
  isSelf: boolean;
  teamIds: string[];
};

export type TransactionContext = {
  txnId?: string;
  playerId?: string;
  amountPence?: number;
  subtype?: "MatchFee" | "MembershipFee" | "Fine" | "TourFee" | "Custom" | string;
};

export type FineEventContext = {
  fineEventId?: string;
  fixtureId?: string;
  seasonId?: string;
  teamId: string;
  playerId: string;
  isPayable: boolean;
};

export type RouteId =
  | "home"
  | "fixtures"
  | "fixtureDetail"
  | "squad"
  | "playerProfile"
  | "stats"
  | "announcements"
  | "settings"
  | "myPayments"
  | "finesFeed"
  | "captainSelection"
  | "captainMatchEntry"
  | "financeOverview"
  | "financePlayerAccounts"
  | "financeTransactions"
  | "financeExports"
  | "adminPlayerManagement"
  | "adminInvites"
  | "adminMembersRoles"
  | "adminSeasons"
  | "adminClubSettings";

export type NavItem = {
  id: RouteId;
  label: string;
  icon?: string; // if you have an icon system; keep as string
  section?: "main" | "more" | "captain" | "finance" | "admin";
  // optional badge (e.g. outstanding balance)
  badge?: number | string;
};

export type ActionId =
  | "availability:setSelf"
  | "selection:write"
  | "matchEntry:write"
  | "fixture:edit"
  | "player:create"
  | "player:edit"
  | "announcement:write"
  | "fineEvent:create"
  | "fineEvent:edit"
  | "transaction:write"
  | "export:generate"
  | "invite:create"
  | "invite:revoke"
  | "memberRole:assign"
  | "season:manage"
  | "clubSettings:edit";

export function hasRole(m: MemberContext, role: Role): boolean {
  return m.roles.includes(role);
}

export function hasAnyRole(m: MemberContext, roles: Role[]): boolean {
  return roles.some((r) => m.roles.includes(r));
}

export function isCaptainForTeam(m: MemberContext, teamId: string): boolean {
  return hasRole(m, "Admin") || (hasRole(m, "Captain") && m.captainTeamIds.includes(teamId));
}

export function canSeeFinance(m: MemberContext): boolean {
  return m.modules.financeEnabled && hasAnyRole(m, ["Treasurer", "Admin"]);
}

export function canSeeFines(m: MemberContext): boolean {
  return m.modules.finesEnabled;
}

/**
 * Route access gating (for route guards).
 * Note: For team-scoped routes (captain pages), you should also check team ownership
 * at the page level once fixture/team is known.
 */
export function canAccessRoute(m: MemberContext, route: RouteId): boolean {
  // Core routes everyone can access as long as they’re a member of the club
  const core: RouteId[] = [
    "home",
    "fixtures",
    "fixtureDetail",
    "squad",
    "playerProfile",
    "stats",
    "announcements",
    "settings",
  ];
  if (core.includes(route)) return true;

  if (route === "myPayments") return m.modules.financeEnabled; // player-only view; still ok to "access"
  if (route === "finesFeed") return m.modules.finesEnabled;

  // Captain routes
  if (route === "captainSelection" || route === "captainMatchEntry") {
    return hasAnyRole(m, ["Captain", "Admin"]);
  }

  // Treasurer routes
  if (
    route === "financeOverview" ||
    route === "financePlayerAccounts" ||
    route === "financeTransactions" ||
    route === "financeExports"
  ) {
    return canSeeFinance(m);
  }

  // Admin routes
  if (
    route === "adminPlayerManagement" ||
    route === "adminInvites" ||
    route === "adminMembersRoles" ||
    route === "adminSeasons" ||
    route === "adminClubSettings"
  ) {
    return hasRole(m, "Admin");
  }

  return false;
}

/**
 * Action gating (for buttons + UI capabilities).
 * Keep this conservative; backend rules/functions must enforce too.
 */
export function canPerformAction(
  m: MemberContext,
  action: ActionId,
  ctx?: {
    fixture?: FixtureContext;
    player?: PlayerContext;
    txn?: TransactionContext;
    fine?: FineEventContext;
  }
): boolean {
  switch (action) {
    case "availability:setSelf": {
      // Only the user can set their own availability.
      // You’ll validate ownership via player.isSelf OR via playerId mapping elsewhere.
      return true;
    }

    case "selection:write": {
      const teamId = ctx?.fixture?.teamId;
      if (!teamId) return hasRole(m, "Admin"); // fallback
      return isCaptainForTeam(m, teamId);
    }

    case "matchEntry:write": {
      const teamId = ctx?.fixture?.teamId;
      if (!teamId) return hasRole(m, "Admin");
      return isCaptainForTeam(m, teamId);
    }

    case "fixture:edit": {
      return hasRole(m, "Admin");
    }

    case "player:create": {
      // V1 suggestion: Admin-only to avoid duplicates.
      return hasRole(m, "Admin");
    }

    case "player:edit": {
      // Admin can edit anyone. Optionally allow self-edit of limited fields elsewhere.
      return hasRole(m, "Admin");
    }

    case "announcement:write": {
      return hasRole(m, "Admin");
    }

    case "fineEvent:create": {
      if (!m.modules.finesEnabled) return false;
      const teamId = ctx?.fine?.teamId ?? ctx?.fixture?.teamId;
      if (!teamId) return hasRole(m, "Admin");
      // Captain/admin only (team scoped)
      return isCaptainForTeam(m, teamId);
    }

    case "fineEvent:edit": {
      if (!m.modules.finesEnabled) return false;
      // Conservative for V1: admin only (or allow captain before lock later)
      return hasRole(m, "Admin");
    }

    case "transaction:write": {
      return canSeeFinance(m);
    }

    case "export:generate": {
      return canSeeFinance(m);
    }

    case "invite:create":
    case "invite:revoke":
    case "memberRole:assign":
    case "season:manage":
    case "clubSettings:edit": {
      return hasRole(m, "Admin");
    }

    default:
      return false;
  }
}

/**
 * Navigation builder. Use this to render tabs + drawer.
 * You can adjust labels/icons to match your UI.
 */
export function buildNav(m: MemberContext, opts?: { paymentsBadge?: number | string }): NavItem[] {
  const items: NavItem[] = [
    { id: "home", label: "Home", section: "main", icon: "home" },
    { id: "fixtures", label: "Fixtures", section: "main", icon: "calendar" },
    { id: "stats", label: "Stats", section: "main", icon: "chart" },
    { id: "squad", label: "Squad", section: "main", icon: "users" },

    // Drawer / More
    { id: "announcements", label: "Announcements", section: "more", icon: "megaphone" },
  ];

  if (m.modules.financeEnabled) {
    items.push({
      id: "myPayments",
      label: "My Payments",
      section: "more",
      icon: "wallet",
      badge: opts?.paymentsBadge,
    });
  }

  if (m.modules.finesEnabled) {
    items.push({ id: "finesFeed", label: "Fines", section: "more", icon: "receipt" });
  }

  items.push({ id: "settings", label: "Settings", section: "more", icon: "cog" });

  // Captain section
  if (hasAnyRole(m, ["Captain", "Admin"])) {
    items.push({ id: "captainSelection", label: "Team Selection", section: "captain", icon: "checklist" });
    items.push({ id: "captainMatchEntry", label: "Match Entry", section: "captain", icon: "edit" });
  }

  // Finance section
  if (canSeeFinance(m)) {
    items.push({ id: "financeOverview", label: "Finance Overview", section: "finance", icon: "bank" });
    items.push({ id: "financePlayerAccounts", label: "Player Accounts", section: "finance", icon: "id" });
    items.push({ id: "financeTransactions", label: "Transactions", section: "finance", icon: "swap" });
    items.push({ id: "financeExports", label: "Exports", section: "finance", icon: "download" });
  }

  // Admin section
  if (hasRole(m, "Admin")) {
    items.push({ id: "adminPlayerManagement", label: "Player Management", section: "admin", icon: "user-plus" });
    items.push({ id: "adminInvites", label: "Invites", section: "admin", icon: "link" });
    items.push({ id: "adminMembersRoles", label: "Members & Roles", section: "admin", icon: "shield" });
    items.push({ id: "adminSeasons", label: "Seasons", section: "admin", icon: "layers" });
    items.push({ id: "adminClubSettings", label: "Club Settings", section: "admin", icon: "sliders" });
  }

  return items;
}

/**
 * Utility: Which fixtures should the user see by default?
 * - Admin/Treasurer: all teams
 * - Otherwise: user's teamIds
 */
export function defaultVisibleTeamIds(m: MemberContext): string[] | "ALL" {
  if (hasAnyRole(m, ["Admin", "Treasurer"])) return "ALL";
  return m.teamIds;
}

/**
 * Utility: Whether user can view a given fixture (team-scoped read).
 * Most clubs allow everyone to see everything; if you want strict team privacy,
 * enforce it here.
 */
export function canViewFixture(m: MemberContext, _fixture: FixtureContext): boolean {

  if (hasAnyRole(m, ["Admin", "Treasurer"])) return true;
  // If you want open visibility, return true here.
  // For stricter privacy, uncomment:
  // return m.teamIds.includes(fixture.teamId);
  return true;
}

/**
 * Example route guard usage:
 *
 * if (!canAccessRoute(memberCtx, routeId)) redirect("/home");
 *
 * Example button gating:
 * const canSave = canPerformAction(memberCtx, "selection:write", { fixture });
 */
